import React, { useId, useState } from 'react';
import Image from 'next/legacy/image';

import {
  TextInputWrapper,
  InputLabel,
  Input,
  ErrorMessage,
  IconWrapper
} from '../TextInput/style';

import { BaseInputProps, TextInputWidth } from '../shared';

import icon from '@/images/error_icon.svg';

interface DateProps extends BaseInputProps {
  borderColor?: string;
  handleOnChange: (event: React.ChangeEvent, value: string) => void;
}

export function DateInput({
  value,
  label,
  placeholder = 'MM / DD / YYYY',
  error,
  required,
  autoComplete,
  handleOnChange,
  disabled = false,
  width = TextInputWidth.FULL,
  borderColor = '#00D9B4'
}: DateProps) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<any>(value ?? undefined);
  const inputId = useId();

  const formatInputValue = (val: string) => {
    const cleanValue = val.replace(/\D/g, '').slice(0, 8); // Only allow 8 digits
    const parts = cleanValue.match(/(\d{0,2})(\d{0,2})(\d{0,4})/);
    if (!parts) return '';
    let formatted = '';
    if (parts[1]) formatted += parts[1];
    if (parts[2]) formatted += ' / ' + parts[2];
    if (parts[3]) formatted += ' / ' + parts[3];
    return formatted;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatInputValue(e.currentTarget.value);
    setInputValue(formattedValue);
    if (handleOnChange) handleOnChange(e, formattedValue);
  };

  return (
    <>
      <TextInputWrapper width={width} error={!!error} borderColor={borderColor}>
        <InputLabel htmlFor={inputId} floatLabel={!!inputValue || isFocused}>
          {label} {required ? '*' : ''}
        </InputLabel>
        <Input
          id={inputId}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          floatLabel={!!inputValue || isFocused}
          autoComplete={autoComplete ?? 'off'}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          aria-required={required}
          aria-invalid={!!error}
          data-testid={`input-date-${label}`}
          inputMode="numeric" // Mobile number keyboard
        />
      </TextInputWrapper>
      <ErrorMessage data-testid={`input-date-${label}-error`} visible={!!error}>
        <IconWrapper>
          <Image src={icon} alt="Form input error" />
        </IconWrapper>
        {error || ''}
      </ErrorMessage>
    </>
  );
}
